import React, {useEffect, useState} from "react";
import {Button, Card, Descriptions, Spin, Statistic, Switch, Table, Tabs, Tag} from "antd";
import {message} from "../../utils/antdFeedback";
import {ArrowLeftOutlined} from "@ant-design/icons";
import {Users} from "lucide-react";
import {useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {getGroupSavingsDetails, updateGroupSavingsLiquidationEmailSetting} from "../../redux/slice/groupSavingsSlice";
import {AppDispatch, RootState} from "../../redux/store";
import {HeroHeader, formatNaira, FONTS, COLORS} from "../../designSystem";

const money = (value: any) => formatNaira(value);
const moneyCell = (value: any) => <span className="font-mono">{formatNaira(value)}</span>;
const monoValue = {fontFamily: FONTS.mono};
const dateText = (value?: string | null) => value ? new Date(value).toLocaleString() : "None";

function GroupSavingsDetails() {
    const {id} = useParams();
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const {token} = useSelector((state: RootState) => state.account);
    const {selectedGroupSavings: group, detailLoading} = useSelector((state: RootState) => state.groupSavings);
    const [savingEmailSetting, setSavingEmailSetting] = useState(false);
    const isCurrentGroup = group && String(group.id) === String(id);

    useEffect(() => {
        if (token && id) {
            dispatch(getGroupSavingsDetails({token, groupId: id}));
        }
    }, [token, id, dispatch]);

    if (detailLoading || !isCurrentGroup) {
        return (
            <AdminLayout>
                <div className="m-10"><Spin tip="Loading group savings..."/></div>
            </AdminLayout>
        );
    }

    const updateLiquidationEmailSetting = async (checked: boolean) => {
        if (!token || !id) return;

        setSavingEmailSetting(true);
        try {
            await dispatch(updateGroupSavingsLiquidationEmailSetting({
                token,
                groupId: id,
                allUserLiquidationRequestEmail: checked
            })).unwrap();
            message.success("Liquidation email setting updated.");
        } catch (error: any) {
            message.error(error?.message || "Unable to update liquidation email setting.");
        } finally {
            setSavingEmailSetting(false);
        }
    };

    return (
        <AdminLayout>
            <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
                <Button className="mb-4" icon={<ArrowLeftOutlined/>} onClick={() => navigate(-1)}>Back</Button>
                <HeroHeader
                    icon={Users}
                    title={group.name}
                    subtitle={group.description || "No description"}
                    actions={<Tag color={group.status === "Active" ? "green" : "red"}>{group.status}</Tag>}
                />

                <section className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <Card size="small"><Statistic title="Total Contributed" valueStyle={monoValue} value={money(group.totalContributed)}/></Card>
                    <Card size="small"><Statistic title="Book Balance" valueStyle={monoValue} value={money(group.bookBalance)}/></Card>
                    <Card size="small"><Statistic title="Active Balance" valueStyle={monoValue} value={money(group.activeBalance)}/></Card>
                    <Card size="small"><Statistic title="Members" value={`${group.activeMemberCount || 0}/${group.memberCount || 0}`}/></Card>
                </section>

                <Card className="mt-6" size="small">
                    <Descriptions bordered column={{xs: 1, md: 2}}>
                        <Descriptions.Item label="Account Name">{group.accountName || "Not available"}</Descriptions.Item>
                        <Descriptions.Item label="Account Number">{group.accountNumber || "Not available"}</Descriptions.Item>
                        <Descriptions.Item label="Bank">{group.bankName || "Not available"}</Descriptions.Item>
                        <Descriptions.Item label="Created">{dateText(group.dateCreated)}</Descriptions.Item>
                        <Descriptions.Item label="Last Contribution">{dateText(group.lastContributionAt)}</Descriptions.Item>
                        <Descriptions.Item label="Categories">{group.categoryCount || 0}</Descriptions.Item>
                        <Descriptions.Item label="Email all users on liquidation request">
                            <Switch
                                checked={!!group.allUserLiquidationRequestEmail}
                                loading={savingEmailSetting}
                                onChange={updateLiquidationEmailSetting}
                            />
                        </Descriptions.Item>
                    </Descriptions>
                </Card>

                <Card className="mt-6">
                    <Tabs
                        items={[
                            {
                                key: "categories",
                                label: "Categories",
                                children: (
                                    <Table
                                        rowKey="id"
                                        dataSource={group.categories || []}
                                        columns={[
                                            {title: "Category", dataIndex: "name", key: "name"},
                                            {title: "Type", dataIndex: "type", key: "type"},
                                            {title: "Frequency", dataIndex: "frequency", key: "frequency"},
                                            {title: "Target", dataIndex: "targetAmount", key: "targetAmount", render: moneyCell},
                                            {title: "Contributed", dataIndex: "totalContributed", key: "totalContributed", render: moneyCell},
                                            {title: "Members", key: "members", render: (_: any, row: any) => `${row.completedMemberCount || 0}/${row.memberCount || 0}`},
                                            {title: "Last Contribution", dataIndex: "lastContributionAt", key: "lastContributionAt", render: dateText},
                                            {title: "Action", key: "action", render: (_: any, row: any) => <Button size="small" onClick={() => navigate(`/saving-groups/categories/${row.id}`)}>Review</Button>},
                                        ]}
                                    />
                                ),
                            },
                            {
                                key: "members",
                                label: "Members",
                                children: (
                                    <Table
                                        rowKey="savingsMemberId"
                                        dataSource={group.members || []}
                                        columns={[
                                            {title: "Member", dataIndex: "fullName", key: "fullName"},
                                            {title: "Email", dataIndex: "email", key: "email"},
                                            {title: "Role", key: "role", render: (_: any, row: any) => row.isAdmin ? <Tag color="blue">Admin</Tag> : row.isValidator ? <Tag>Validator</Tag> : <Tag>User</Tag>},
                                            {title: "Status", key: "status", render: (_: any, row: any) => <Tag color={row.isActive ? "green" : "red"}>{row.isActive ? "Active" : "Inactive"}</Tag>},
                                            {title: "Contributed", dataIndex: "totalContributed", key: "totalContributed", render: moneyCell},
                                            {title: "Joined", dataIndex: "dateJoined", key: "dateJoined", render: dateText},
                                        ]}
                                    />
                                ),
                            },
                            {
                                key: "contributions",
                                label: "Recent Contributions",
                                children: (
                                    <Table
                                        rowKey="id"
                                        dataSource={group.recentContributions || []}
                                        columns={[
                                            {title: "Member", dataIndex: "memberName", key: "memberName"},
                                            {title: "Email", dataIndex: "memberEmail", key: "memberEmail"},
                                            {title: "Category", dataIndex: "categoryName", key: "categoryName"},
                                            {title: "Amount", dataIndex: "amountPaid", key: "amountPaid", render: moneyCell},
                                            {title: "Date", dataIndex: "datePaid", key: "datePaid", render: dateText},
                                            {title: "Reference", dataIndex: "transactionReference", key: "transactionReference"},
                                        ]}
                                    />
                                ),
                            },
                        ]}
                    />
                </Card>
            </main>
        </AdminLayout>
    );
}

export default GroupSavingsDetails;
